import { useCallback } from 'react';

import { MedicalChamber, MedicalChamberQuery } from '@dreryk/egabinet-healthcare-provider-frontend-api';
import {
   RpwdlCannotImportGroupPracticeFault,
   RpwdlHealthcarePractice,
   RpwdlHealthcarePracticeQuery,
   RpwdlTooManyQueriesFault,
} from '@dreryk/egabinet-rpwdl-integration-frontend-api';

import { performCqrsQueryFind, performCqrsQuerySearch } from '@dreryk/command-query-react-common';
import { isFaultType } from '@dreryk/command-query-ws-client';
import {
   Button,
   dispatchSnackbarInformativeEvent,
   dispatchSnackbarNegativeEvent,
   dispatchSnackbarPositiveEvent,
   StyledMargin,
} from '@dreryk/react-components';
import { Slot } from '@dreryk/watermelon';

import { healthcarePracticeTranslationKeys } from '../../../translations';
import { ServiceProvidingLocationFormBean } from '../common/serviceProvidingLocation';

import {
   HealthcarePracticeUpdateFormBean,
   HealthcarePracticeUpdateFormController,
} from './HealthcarePracticeUpdateForm.types';

type HealthcarePracticeFromRpwdlButtonProps = Pick<
   HealthcarePracticeUpdateFormBean,
   'registerBookNumber' | 'medicalChamber'
> &
   Pick<HealthcarePracticeUpdateFormController, 'reset' | 'getValue'>;

// eslint-disable-next-line max-lines-per-function
export const HealthcarePracticeFromRpwdlButton = (props: HealthcarePracticeFromRpwdlButtonProps): JSX.Element => {
   const { registerBookNumber, medicalChamber, reset } = props;

   const medicalChamberGetter = useCallback<(code?: number) => Promise<MedicalChamber | null>>(async (code) => {
      const medicalChambers = await performCqrsQuerySearch<MedicalChamber>(() => new MedicalChamberQuery());
      return medicalChambers.find((chamber) => chamber.code === code) ?? null;
   }, []);

   const handleHealthcarePracticeImport = useCallback(
      async (healthcarePracticeFromRpwdl: RpwdlHealthcarePractice) =>
         reset({
            registerBookNumber: healthcarePracticeFromRpwdl.registerBookNumber,
            medicalChamber: await medicalChamberGetter(healthcarePracticeFromRpwdl.medicalChamberCode),
            name: healthcarePracticeFromRpwdl.name ?? null,
            city: healthcarePracticeFromRpwdl.address?.findTown()?.getName() ?? null,
            postcode: healthcarePracticeFromRpwdl.address?.findPostcode() ?? null,
            street: healthcarePracticeFromRpwdl.address?.findStreet()?.asString() ?? null,
            house: healthcarePracticeFromRpwdl.address?.findBuilding()?.asString() ?? null,
            flat: healthcarePracticeFromRpwdl.address?.findFlat()?.asString() ?? null,
            phoneNumber: healthcarePracticeFromRpwdl.phoneNumber ?? null,
            emailAddress: healthcarePracticeFromRpwdl.emailAddress ?? null,
            regon: healthcarePracticeFromRpwdl.regon?.asString() ?? null,
            logo: props.getValue('logo'),
            taxIdentifier: healthcarePracticeFromRpwdl.taxIdentifier?.asString() ?? null,
            serviceProvidingLocationsToUpdate:
               healthcarePracticeFromRpwdl.serviceProvidingLocations?.map(
                  (serviceProvidingLocation): ServiceProvidingLocationFormBean => ({
                     locationCode: serviceProvidingLocation.locationCode ?? null,
                     city: serviceProvidingLocation.address?.findTown()?.getName() ?? null,
                     emailAddress: serviceProvidingLocation.emailAddress ?? null,
                     flat: serviceProvidingLocation.address?.findFlat()?.asString() ?? null,
                     house: serviceProvidingLocation.address?.findBuilding()?.asString() ?? null,
                     serviceProvidingLocationName: serviceProvidingLocation.name ?? null,
                     phoneNumber: serviceProvidingLocation.phoneNumber ?? null,
                     postcode: serviceProvidingLocation.address?.findPostcode() ?? null,
                     street: serviceProvidingLocation.address?.findStreet()?.asString() ?? null,
                     usingHealthcarePracticeDetails: false,
                     medicalField: null,
                  })
               ) ?? [],
         }),
      [reset, medicalChamberGetter, props]
   );
   const handleClick = useCallback(async () => {
      try {
         const healthcarePractice = await performCqrsQueryFind<RpwdlHealthcarePractice>(() => {
            const query = new RpwdlHealthcarePracticeQuery();
            if (registerBookNumber) {
               query.registerBookNumber = registerBookNumber;
            }

            if (medicalChamber) {
               query.medicalChamberCode = Slot.of(medicalChamber.code);
            }

            return query;
         });

         if (healthcarePractice) {
            await handleHealthcarePracticeImport(healthcarePractice);

            return dispatchSnackbarPositiveEvent(healthcarePracticeTranslationKeys.dataImportFromRpwdlWasSuccessful);
         }

         return dispatchSnackbarNegativeEvent(
            healthcarePracticeTranslationKeys.incorrectRegistrationInformationWasEntered
         );
      } catch (error: unknown) {
         if (isFaultType(error as Error, new RpwdlCannotImportGroupPracticeFault().class)) {
            return dispatchSnackbarInformativeEvent(
               healthcarePracticeTranslationKeys.cannotImportGroupPracticeFromRpwdl
            );
         }

         if (isFaultType(error as Error, new RpwdlTooManyQueriesFault().class)) {
            return dispatchSnackbarNegativeEvent(healthcarePracticeTranslationKeys.tooManyQueriesToRpwdl);
         }

         return dispatchSnackbarNegativeEvent(healthcarePracticeTranslationKeys.dataImportFromRpwdlEndedWithError);
      }
   }, [handleHealthcarePracticeImport, medicalChamber, registerBookNumber]);

   return (
      <StyledMargin top={26}>
         <Button
            label={healthcarePracticeTranslationKeys.getHealthcarePracticeFormRpwdl}
            onClick={handleClick}
            disabled={!registerBookNumber}
            testId={'getHealthcarePracticeFromRpwdl'}
            type={'button'}
         />
      </StyledMargin>
   );
};
